import {useMutation, useQuery, useQueryClient} from "react-query";
import axios from "axios";
import {PagedSearchParams} from "@atiautomacao/ati-ui-library";
import {Ticket, TicketSendMessage, TicketAttachment, TicketSendAttachment} from "../../../Shared/Types/Ticket";
import {useSnackbar} from "notistack";

const apiUrl = "api/ticket";
const timeout = 30000;

const handleAccepted = (res: any) => res.data;
const handleRejected = (err: any) => console.error("Error fetching data:", err);

export const OpenTicket = () => {
    const { enqueueSnackbar } = useSnackbar();
    return useMutation(
        (data: Ticket) => axios.post(apiUrl, data, {timeout}).then(handleAccepted), 
        {
            onSuccess: () => {
                enqueueSnackbar("Ticket aberto com sucesso!", { variant: "success" });
            },
            onError: (error: any) => {
                let message = error?.response?.data?.detail;

                // O backend responde 400 para várias restrições; só este detail indica data no passado.
                if(error?.response?.status === 400 && message === "Ticket scheduling less than current date"){
                    message = "Data de agendamento menor do que o dia atual"
                }
                enqueueSnackbar(`Erro ao abrir ticket: ${message}`, { variant: "error" });
            }
        }
    );
};

export const UpdateTicket = () => {
    const { enqueueSnackbar } = useSnackbar();
    const queryClient = useQueryClient();
    return useMutation(
        (data: Ticket) => axios.put(apiUrl, data, {timeout}).then(handleAccepted),
        {
            onSuccess: () => {
                enqueueSnackbar("Ticket alterado com sucesso!", { variant: "success" });
                queryClient.invalidateQueries('search-tickets');
                queryClient.invalidateQueries('ticket-alarms');
            },
            onError: (error: any) => {
                let message = error?.response?.data?.detail;

                if(error?.response?.status === 409){
                    message = "Ticket já foi fechado"
                }
                enqueueSnackbar(`Erro ao alterar ticket: ${message}`, { variant: "error" });

            }
        }
    );
};

export const CloseTicket = () => {
    const { enqueueSnackbar } = useSnackbar();
    const queryClient = useQueryClient();
    return useMutation(
        (id: number) => axios.delete(`${apiUrl}/${id}`, {timeout}).then(handleAccepted),
        {
            onSuccess: () => {
                enqueueSnackbar("Ticket encerrado com sucesso!", { variant: "success" });
            },
            onError: (error: any) => {
                let message = error?.response?.data?.detail;

                if(error?.response?.status === 409){
                    message = "Ticket já foi fechado"
                }
                if(!message){
                    message = "Não foi possível concluir a operação"
                }
                enqueueSnackbar(`Erro ao encerrar ticket: ${message}`, { variant: "error" });
            },
            onSettled: () => {
                queryClient.invalidateQueries('search-tickets');
                queryClient.invalidateQueries('findbyid-ticket');
                queryClient.invalidateQueries('findallby-alarmid');
            }
        }
    );
};

export const FindByIdTicket = (id?: number | null) => {
    return useQuery(
        ["findbyid-ticket", id],
        () => axios.get(`${apiUrl}/${id}`, {timeout}).then(handleAccepted, handleRejected),
        {enabled: !!id}
    );
};

export const FindAllByAlarmId = (
    alarmId?: number | null,
    permission?: boolean,
    isActive?: boolean
) => {
    return useQuery(
        ["findallby-alarmid", alarmId],
        () => axios.get(`${apiUrl}/findall-by-alarm/${alarmId}`, {timeout}).then(handleAccepted, handleRejected),
        {enabled: !!alarmId && !!permission && !!isActive}
    );
};

export const CountAllByAlarmId = (alarmIds: number[] | null) => {
    const idsParam = alarmIds ? alarmIds.join() : "";
    return useQuery(
        ["countall-by-alarms", idsParam],
        () => axios.get(`${apiUrl}/countall-by-alarms?ids=${idsParam}`, {timeout}).then(handleAccepted, handleRejected),
        {enabled: !!alarmIds}
    );
};

export const SearchTickets = (pagedSearchParams: PagedSearchParams) => {
    const params = pagedSearchParams ? pagedSearchParams.toURLSearchParams() : null;

    return useQuery(
        ["search-tickets", pagedSearchParams],
        () => axios.get(`${apiUrl}/search`, {params, timeout}).then(handleAccepted, handleRejected)
    );
};

export const FindTicketMessages = (ticketId?: number) => {
    return useQuery(
        ['ticket-messages', ticketId],
        () => axios.get(`${apiUrl}/${ticketId}/messages`, {timeout}).then(handleAccepted, handleRejected),
        {enabled: !!ticketId}
    );
};

export const SendTicketMessage = (ticketId?: number) => {
    const queryClient = useQueryClient();
    return useMutation(
        (data: TicketSendMessage) => axios.post(`${apiUrl}/${ticketId}/messages`, data, {timeout}).then(handleAccepted, handleRejected),
        {
            onSuccess: () => queryClient.invalidateQueries('ticket-messages'),
        }
    );
};

export const FindTicketAttachments = (ticketId?: number) => {
    return useQuery(
        ["ticket-attachments", ticketId],
        () => axios.get(`${apiUrl}/${ticketId}/attachments`, {timeout}).then(handleAccepted, handleRejected),
        {enabled: !!ticketId}
    );
};

export const SendTicketAttachment = (ticketId?: number) => {
    const queryClient = useQueryClient();
    const headers = { 'Content-Type': 'multipart/form-data' };
    const send = (formData: FormData) => axios.post(`${apiUrl}/${ticketId}/attachments`, formData, {headers, timeout}).then(handleAccepted, handleRejected);
    return useMutation(send, {
        onSuccess: () => {
            queryClient.invalidateQueries(["ticket-attachments", ticketId]);
            queryClient.invalidateQueries(["ticket-messages", ticketId]);
        },
    });
};

export const RemoveTicketAttachment = (ticketId?: number) => {
    const queryClient = useQueryClient();
    const remove = (ticketAttachmentId: number) => axios.delete(`${apiUrl}/${ticketId}/attachments/${ticketAttachmentId}`, {timeout}).then(handleAccepted, handleRejected);
    return useMutation(remove, {
        onSuccess: () => {
            queryClient.invalidateQueries(["ticket-attachments", ticketId]);
            queryClient.invalidateQueries(["ticket-messages", ticketId]);
        },
    });
};

export const DownloadTicketAttachment = (ticketAttachment: TicketAttachment) => {
    // Realiza a requisição HTTP para obter o arquivo
    axios.get(`${apiUrl}/${ticketAttachment.ticketId}/attachments/${ticketAttachment.id}/download`, {
      responseType: 'blob', // Importante para arquivos binários
    }).then((response) => {
        // Criar um link para realizar o download do arquivo
        const blob = new Blob([response.data], { type: response.headers['content-type'] });
        const link = document.createElement('a');
        const url = window.URL.createObjectURL(blob);

        // Definir o nome do arquivo no download
        link.href = url;
        link.setAttribute('download', ticketAttachment.fileName);

        // Acionar o clique no link para iniciar o download
        document.body.appendChild(link);
        link.click();

        // Limpar o link após o download
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);
    }, (error) => {
        console.error('Erro ao fazer o download do arquivo:', error);
    });
};

export const FindTicketAlarms = (ticketId?: number | null) => {
    return useQuery(
        ["ticket-alarms", ticketId],
        () => axios.get(`${apiUrl}/${ticketId}/alarms`, {timeout}).then(handleAccepted, handleRejected),
        {enabled: !!ticketId}
    );
};
